import React, { useState, useRef } from 'react';

import { PageMeta } from '../../components';

const ZIP_RE = { CA: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/, US: /^\d{5}$/ };

/**
 * Looks up a zip/postal code without touching any component state, so the
 * caller decides how to apply the result — this is what lets both the
 * onBlur handler and the Continue button share one staleness-safe path
 * instead of each racing to write `zipVerified` from a stale closure.
 */
async function verifyZip(zip, country) {
  const pattern = ZIP_RE[country];
  if (!pattern || !pattern.test(zip)) {
    return { status: zip ? 'invalid' : 'idle' };
  }
  try {
    const resp = await fetch(
      `/api/zip-lookup?code=${encodeURIComponent(zip)}&country=${country === 'US' ? 'us' : 'ca'}`
    );
    const data = await resp.json();
    return data.success ? { status: 'valid', city: data.result.city } : { status: 'invalid' };
  } catch {
    // Lookup service hiccup shouldn't block a lead — format is already validated locally.
    return { status: 'unavailable' };
  }
}

const c = {
  teal: '#1A9E85',
  tealDk: '#157A68',
  navy: '#0F2033',
  border: '#DDE7EE',
  bg: '#F5F9FC',
  white: '#FFFFFF',
  text: '#1B2B3A',
  muted: '#7A8C99',
  red: '#DC2626',
};

const PLANS = [
  {
    id: 'Dealer Starter',
    label: 'Starter',
    price: '$0',
    period: '/mo',
    note: 'First 100 dealers — locks in at $999.99/mo after launch',
    listings: '25 vehicles',
    highlight: false,
    badge: '🐦 Early Bird',
    features: [
      'Dealer storefront',
      '25 active listings',
      'AI descriptions',
      'Lead inbox',
      'Verified badge',
    ],
  },
  {
    id: 'Dealer Pro',
    label: 'Pro',
    price: '$999.99',
    period: '/mo',
    note: 'Most popular for established dealers',
    listings: '100 vehicles',
    highlight: true,
    badge: 'Most Popular',
    features: [
      'Everything in Starter',
      '100 active listings',
      'Priority placement',
      'Buyer matching',
      'CRM export',
      'Account manager',
    ],
  },
  {
    id: 'Enterprise Fleet',
    label: 'Enterprise',
    price: 'Custom',
    period: '',
    note: 'Dealer groups, auctions, fleet operators',
    listings: '1,000 vehicles',
    highlight: false,
    badge: '',
    features: [
      'Everything in Pro',
      '1,000 active listings',
      'Bulk CSV / API sync',
      'Multi-location',
      'Custom financing',
      'SLA uptime',
    ],
  },
];

const PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];

const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
];

const STEPS = ['Your Info', 'Dealership', 'Plan', 'Review'];

// ── Progress Bar ───────────────────────────────────────────────────────────────
function ProgressBar({ step }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 36 }}>
      {STEPS.map((label, i) => (
        <React.Fragment key={i}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flex: i < STEPS.length - 1 ? 'none' : 1,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 700,
                flexShrink: 0,
                background: i < step ? c.teal : i === step ? c.navy : c.border,
                color: i <= step ? '#fff' : c.muted,
              }}
            >
              {i < step ? '✓' : i + 1}
            </div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: i === step ? c.navy : c.muted,
                marginTop: 6,
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </div>
          </div>
          {i < STEPS.length - 1 && (
            <div
              style={{
                flex: 1,
                height: 2,
                background: i < step ? c.teal : c.border,
                margin: '0 6px',
                marginBottom: 20,
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Field ──────────────────────────────────────────────────────────────────────
function Field({ label, error, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label
        style={{ display: 'block', fontSize: 13, fontWeight: 600, color: c.navy, marginBottom: 6 }}
      >
        {label}
      </label>
      {children}
      {error && <div style={{ fontSize: 12, color: c.red, marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function Input({ value, onChange, type = 'text', placeholder, ...rest }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '11px 14px',
        fontSize: 14,
        border: `1px solid ${c.border}`,
        borderRadius: 10,
        color: c.text,
        background: c.white,
        outline: 'none',
        boxSizing: 'border-box',
        fontFamily: 'inherit',
      }}
      {...rest}
    />
  );
}

function Select({ value, onChange, children }) {
  return (
    <select
      value={value}
      onChange={onChange}
      style={{
        width: '100%',
        padding: '11px 14px',
        fontSize: 14,
        border: `1px solid ${c.border}`,
        borderRadius: 10,
        color: c.text,
        background: c.white,
        outline: 'none',
        boxSizing: 'border-box',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </select>
  );
}

// ── Steps ──────────────────────────────────────────────────────────────────────

function Step1({ form, setForm, errors }) {
  return (
    <div>
      <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800, color: c.navy }}>
        Tell us about yourself
      </h2>
      <p style={{ margin: '0 0 28px', fontSize: 14, color: c.muted, lineHeight: 1.6 }}>
        Your contact details — we'll use these to verify your dealership and set up your account.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <Field label="First name" error={errors.firstName}>
          <Input
            value={form.firstName}
            onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
            placeholder="Jane"
          />
        </Field>
        <Field label="Last name" error={errors.lastName}>
          <Input
            value={form.lastName}
            onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
            placeholder="Smith"
          />
        </Field>
      </div>
      <Field label="Business email" error={errors.email}>
        <Input
          type="email"
          value={form.email}
          onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
          placeholder="jane@sunrisemotors.ca"
        />
      </Field>
      <Field label="Phone number" error={errors.phone}>
        <Input
          type="tel"
          value={form.phone}
          onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
          placeholder="+1 (416) 555-0100"
        />
      </Field>
      <Field label="Password" error={errors.password}>
        <Input
          type="password"
          value={form.password}
          onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
          placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
          autoComplete="new-password"
          aria-label="Password"
        />
      </Field>
      <Field label="Confirm password" error={errors.passwordConfirm}>
        <Input
          type="password"
          value={form.passwordConfirm}
          onChange={e => setForm(f => ({ ...f, passwordConfirm: e.target.value }))}
          placeholder="Re-enter your password"
          autoComplete="new-password"
          aria-label="Confirm password"
        />
      </Field>
      <p style={{ margin: '-6px 0 22px', fontSize: 12.5, color: c.muted, lineHeight: 1.6 }}>
        You'll use this to sign in to your dealer dashboard. Your account is created straight away
        so you can connect inventory while we verify your licence — nothing goes live to buyers
        until that's done.
      </p>
      <Field label="Role at dealership">
        <Select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
          <option>Owner / GM</option>
          <option>Sales Manager</option>
          <option>Internet Manager</option>
          <option>Marketing Manager</option>
          <option>Wholesaler</option>
          <option>Other</option>
        </Select>
      </Field>
    </div>
  );
}

function Step2({ form, setForm, errors }) {
  async function handleZipBlur(rawZip) {
    const zip = rawZip.trim();
    const country = form.country;
    // Only the applicant's most recent edit should ever be allowed to set
    // zipVerified — a slow response for a code they've since changed (or a
    // country they've since switched away from) must not land.
    const isStillCurrent = f => f.zip.trim() === zip && f.country === country;

    setForm(f => (isStillCurrent(f) ? { ...f, zipVerified: zip ? 'checking' : 'idle' } : f));
    if (!zip) return;

    const result = await verifyZip(zip, country);
    setForm(f => {
      if (!isStillCurrent(f)) return f; // stale: input moved on while this request was in flight
      return {
        ...f,
        zipVerified: result.status,
        city: result.status === 'valid' && !f.city.trim() ? result.city : f.city,
      };
    });
  }

  return (
    <div>
      <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800, color: c.navy }}>
        Your dealership
      </h2>
      <p style={{ margin: '0 0 28px', fontSize: 14, color: c.muted, lineHeight: 1.6 }}>
        This info powers your public channel page on SkipTheDealerships.ai.
      </p>
      <Field label="Dealership name" error={errors.dealership}>
        <Input
          value={form.dealership}
          onChange={e => setForm(f => ({ ...f, dealership: e.target.value }))}
          placeholder="Sunrise Motors Inc."
        />
      </Field>
      <Field label="Country">
        <Select
          value={form.country}
          onChange={e => {
            const country = e.target.value;
            setForm(f => ({
              ...f,
              country,
              province: country === 'CA' ? f.province || 'ON' : '',
              state: country === 'US' ? f.state || 'CA' : '',
              zip: '',
              zipVerified: 'idle',
            }));
          }}
        >
          <option value="CA">Canada</option>
          <option value="US">United States</option>
        </Select>
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <Field label="City" error={errors.city}>
          <Input
            value={form.city}
            onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
            placeholder={form.country === 'US' ? 'Detroit' : 'Winnipeg'}
          />
        </Field>
        {form.country === 'US' ? (
          <Field label="State">
            <Select
              value={form.state}
              onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
            >
              {US_STATES.map(s => (
                <option key={s}>{s}</option>
              ))}
            </Select>
          </Field>
        ) : (
          <Field label="Province">
            <Select
              value={form.province}
              onChange={e => setForm(f => ({ ...f, province: e.target.value }))}
            >
              {PROVINCES.map(p => (
                <option key={p}>{p}</option>
              ))}
            </Select>
          </Field>
        )}
      </div>
      <Field
        label={form.country === 'US' ? 'Zip code' : 'Postal code'}
        error={
          errors.zip ||
          (form.zipVerified === 'invalid'
            ? `Enter a valid ${form.country === 'US' ? 'US zip' : 'Canadian postal'} code`
            : undefined)
        }
      >
        <Input
          value={form.zip}
          onChange={e => setForm(f => ({ ...f, zip: e.target.value, zipVerified: 'idle' }))}
          onBlur={e => handleZipBlur(e.target.value)}
          placeholder={form.country === 'US' ? '48226' : 'R3C 0V8'}
        />
        {form.zipVerified === 'checking' && (
          <div style={{ fontSize: 12, color: c.muted, marginTop: 4 }}>Checking…</div>
        )}
        {form.zipVerified === 'valid' && (
          <div style={{ fontSize: 12, color: c.teal, marginTop: 4 }}>✓ Verified</div>
        )}
      </Field>
      <Field label="Dealer licence number" error={errors.licence}>
        <Input
          value={form.licence}
          onChange={e => setForm(f => ({ ...f, licence: e.target.value }))}
          placeholder="MVDA-12345"
        />
      </Field>
      <Field label="Current inventory size">
        <Select
          value={form.inventory}
          onChange={e => setForm(f => ({ ...f, inventory: e.target.value }))}
        >
          <option>1–25 vehicles</option>
          <option>26–100 vehicles</option>
          <option>101–300 vehicles</option>
          <option>300–1,000 vehicles</option>
          <option>1,000+ vehicles</option>
        </Select>
      </Field>
      <Field label="Website (optional)">
        <Input
          value={form.website}
          onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
          placeholder="https://sunrisemotors.ca"
        />
      </Field>
    </div>
  );
}

function Step3({ form, setForm }) {
  return (
    <div>
      <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800, color: c.navy }}>
        Choose your plan
      </h2>
      <p style={{ margin: '0 0 28px', fontSize: 14, color: c.muted, lineHeight: 1.6 }}>
        Start for free. Upgrade as your inventory grows.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {PLANS.map(plan => {
          const sel = form.tier === plan.id;
          return (
            <div
              key={plan.id}
              onClick={() => setForm(f => ({ ...f, tier: plan.id }))}
              style={{
                border: `2px solid ${sel ? c.teal : c.border}`,
                borderRadius: 14,
                padding: '18px 20px',
                cursor: 'pointer',
                background: sel ? '#F0FDF9' : c.white,
                transition: 'all 0.15s',
                position: 'relative',
              }}
            >
              {plan.badge && (
                <span
                  style={{
                    position: 'absolute',
                    top: -11,
                    left: 20,
                    background: plan.highlight ? c.teal : '#F59E0B',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '3px 10px',
                    borderRadius: 20,
                    letterSpacing: '0.05em',
                  }}
                >
                  {plan.badge}
                </span>
              )}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 10,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 20, fontWeight: 800, color: sel ? c.teal : c.navy }}>
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span style={{ fontSize: 12, color: c.muted }}>{plan.period}</span>
                  )}
                  <span style={{ fontSize: 13, fontWeight: 700, color: c.navy, marginLeft: 8 }}>
                    {plan.label}
                  </span>
                </div>
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    border: `2px solid ${sel ? c.teal : c.border}`,
                    background: sel ? c.teal : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {sel && (
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />
                  )}
                </div>
              </div>
              <div style={{ fontSize: 12, color: c.teal, fontWeight: 600, marginBottom: 6 }}>
                Up to {plan.listings}
              </div>
              <div style={{ fontSize: 12, color: c.muted, marginBottom: 12 }}>{plan.note}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {plan.features.map(f => (
                  <span
                    key={f}
                    style={{
                      fontSize: 11,
                      color: c.muted,
                      background: c.bg,
                      border: `1px solid ${c.border}`,
                      borderRadius: 20,
                      padding: '2px 8px',
                    }}
                  >
                    ✓ {f}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Step4({ form }) {
  const plan = PLANS.find(p => p.id === form.tier);
  const rows = [
    ['Name', `${form.firstName} ${form.lastName}`],
    ['Role', form.role],
    ['Email', form.email],
    ['Phone', form.phone || '—'],
    ['Dealership', form.dealership],
    [
      'Location',
      form.country === 'US'
        ? `${form.city}, ${form.state} ${form.zip}`
        : `${form.city}, ${form.province} ${form.zip}`,
    ],
    ['Licence #', form.licence || '—'],
    ['Inventory size', form.inventory],
    ['Website', form.website || '—'],
    ['Plan', `${plan?.label} — ${plan?.price}${plan?.period}`],
  ];

  return (
    <div>
      <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800, color: c.navy }}>
        Review & submit
      </h2>
      <p style={{ margin: '0 0 24px', fontSize: 14, color: c.muted, lineHeight: 1.6 }}>
        Confirm your details below. We'll verify your dealer licence and activate your storefront
        within 1 business day.
      </p>
      <div
        style={{
          background: c.bg,
          border: `1px solid ${c.border}`,
          borderRadius: 14,
          overflow: 'hidden',
          marginBottom: 20,
        }}
      >
        {rows.map(([label, val], i) => (
          <div
            key={label}
            style={{
              display: 'flex',
              padding: '12px 18px',
              borderTop: i > 0 ? `1px solid ${c.border}` : 'none',
            }}
          >
            <div style={{ width: 140, fontSize: 13, color: c.muted, flexShrink: 0 }}>{label}</div>
            <div
              style={{ fontSize: 13, fontWeight: 600, color: label === 'Plan' ? c.teal : c.navy }}
            >
              {val}
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          background: '#FFF9EC',
          border: '1px solid #FDE68A',
          borderRadius: 10,
          padding: '12px 16px',
          fontSize: 13,
          color: '#92400E',
        }}
      >
        By submitting, you confirm you are an authorized representative of this dealership and agree
        to our{' '}
        <a href="/terms-of-service" style={{ color: c.teal }}>
          Terms of Service
        </a>
        .
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
// Sharetribe rejects anything shorter when the account is created, so catching
// it here turns a failed signup into an inline message.
const MIN_PASSWORD_LENGTH = 8;

const DEFAULTS = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  role: 'Owner / GM',
  password: '',
  passwordConfirm: '',
  dealership: '',
  city: '',
  country: 'CA',
  province: 'ON',
  state: '',
  zip: '',
  zipVerified: 'idle',
  licence: '',
  inventory: '1–25 vehicles',
  website: '',
  tier: 'Dealer Pro',
};

function validate(step, form) {
  const e = {};
  if (step === 0) {
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim()) e.lastName = 'Required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (form.password.length < MIN_PASSWORD_LENGTH)
      e.password = `At least ${MIN_PASSWORD_LENGTH} characters`;
    else if (form.password !== form.passwordConfirm) e.passwordConfirm = 'Passwords do not match';
  }
  if (step === 1) {
    if (!form.dealership.trim()) e.dealership = 'Required';
    if (!form.city.trim()) e.city = 'Required';
    const zip = form.zip.trim();
    const pattern = ZIP_RE[form.country];
    if (!zip || !pattern.test(zip)) {
      e.zip = 'Required';
    } else if (form.zipVerified === 'invalid') {
      // A well-formed but nonexistent code (e.g. US 00000) must not pass —
      // format alone isn't proof the code is real.
      e.zip = `Enter a valid ${form.country === 'US' ? 'US zip' : 'Canadian postal'} code`;
    } else if (form.zipVerified !== 'valid' && form.zipVerified !== 'unavailable') {
      // 'unavailable' (the lookup service itself failed) is let through —
      // an outage on our end shouldn't block a lead over an already
      // format-valid code. Anything else means it hasn't been confirmed yet.
      e.zip = 'Verifying — please wait a moment';
    }
  }
  return e;
}

export default function DealerRegisterPage() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(DEFAULTS);
  const [errors, setErrors] = useState({});
  const [submitting, setSub] = useState(false);
  const [done, setDone] = useState(false);
  const [apiError, setApiErr] = useState('');

  // Mirrors `form` so `next()` can read the true current value after an
  // `await` — the `form` captured in next()'s own closure stays frozen at
  // whatever it was when the function was called, even though a setForm
  // triggered by that same call re-renders with a fresh value.
  const formRef = useRef(form);
  formRef.current = form;

  async function next() {
    let effectiveForm = form;

    // The applicant may click Continue without ever blurring the zip field
    // (autofill, Enter key, tabbing straight to the button) — verify it now
    // rather than let validate() reject a code nothing has actually checked.
    if (step === 1 && form.zipVerified !== 'valid' && form.zipVerified !== 'unavailable') {
      const zip = form.zip.trim();
      const country = form.country;
      const isStillCurrent = f => f.zip.trim() === zip && f.country === country;

      setForm(f => (isStillCurrent(f) ? { ...f, zipVerified: zip ? 'checking' : 'idle' } : f));
      const result = await verifyZip(zip, country);

      if (isStillCurrent(formRef.current)) {
        effectiveForm = {
          ...formRef.current,
          zipVerified: result.status,
          city:
            result.status === 'valid' && !formRef.current.city.trim()
              ? result.city
              : formRef.current.city,
        };
        setForm(() => effectiveForm);
      } else {
        // The applicant changed the zip or country while this was in
        // flight — trust whatever is on screen now, not this stale result.
        effectiveForm = formRef.current;
      }
    }

    const e = validate(step, effectiveForm);
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setErrors({});
    setStep(s => s + 1);
  }

  async function submit() {
    setSub(true);
    setApiErr('');
    try {
      const resp = await fetch('/api/dealer-apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${form.firstName} ${form.lastName}`,
          dealership: form.dealership,
          email: form.email,
          // Provisions the dealer's Sharetribe account server-side. Sent once,
          // over the same HTTPS request as the rest of the application, and
          // deliberately absent from the review step and from every log line.
          password: form.password,
          phone: form.phone,
          inventory: form.inventory,
          tier: form.tier,
          city: form.city,
          country: form.country,
          province: form.country === 'CA' ? form.province : '',
          state: form.country === 'US' ? form.state : '',
          zip: form.zip,
          licence: form.licence,
          website: form.website,
          role: form.role,
        }),
      });
      const data = await resp.json();
      if (data.success) setDone(true);
      else
        setApiErr(
          data.error || 'Something went wrong. Email sales@skipthedealerships.ca directly.'
        );
    } catch {
      setApiErr('Network error. Please try again or email sales@skipthedealerships.ca.');
    }
    setSub(false);
  }

  // ── Success screen ────────────────────────────────────────────────────────
  if (done)
    return (
      <>
        <PageMeta
          path="/dealer/register"
          title="Dealer Application Submitted"
          description="Thank you for applying to become a SkipTheDealerships dealer partner."
        />
        <div
          style={{
            minHeight: '100vh',
            background: c.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          <div
            style={{
              background: c.white,
              border: `1px solid ${c.border}`,
              borderRadius: 20,
              padding: '48px 40px',
              maxWidth: 480,
              width: '100%',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: '#F0FDF9',
                border: `2px solid ${c.teal}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                margin: '0 auto 20px',
              }}
            >
              ✅
            </div>
            <h2 style={{ margin: '0 0 10px', fontSize: 24, fontWeight: 800, color: c.navy }}>
              Application sent!
            </h2>
            <p style={{ margin: '0 0 8px', fontSize: 15, color: c.muted, lineHeight: 1.7 }}>
              We'll verify <strong style={{ color: c.navy }}>{form.dealership}</strong>'s dealer
              licence and activate your storefront within{' '}
              <strong style={{ color: c.navy }}>1 business day</strong>.
            </p>
            <p style={{ margin: '0 0 28px', fontSize: 13, color: c.muted }}>
              Confirmation sent to <strong>{form.email}</strong>
            </p>
            <a
              href="/"
              style={{
                display: 'inline-block',
                background: c.teal,
                color: '#fff',
                padding: '12px 28px',
                borderRadius: 10,
                fontWeight: 700,
                fontSize: 14,
                textDecoration: 'none',
              }}
            >
              Back to Marketplace
            </a>
            <div style={{ marginTop: 16 }}>
              <a href="/dealers" style={{ fontSize: 13, color: c.teal, textDecoration: 'none' }}>
                Learn more about the Dealer Program →
              </a>
            </div>
          </div>
        </div>
      </>
    );

  const stepContent = [
    <Step1 form={form} setForm={setForm} errors={errors} />,
    <Step2 form={form} setForm={setForm} errors={errors} />,
    <Step3 form={form} setForm={setForm} />,
    <Step4 form={form} />,
  ][step];

  return (
    <>
      <PageMeta
        path="/dealer/register"
        title="Become a Dealer Partner – SkipTheDealerships"
        description="Apply to become a dealer partner on SkipTheDealerships. Join Canada's fastest-growing direct vehicle marketplace for dealers and wholesalers."
      />
      <div
        style={{
          minHeight: '100vh',
          background: c.bg,
          fontFamily: 'DM Sans, -apple-system, sans-serif',
        }}
      >
        {/* Top bar */}
        <div
          style={{
            background: c.navy,
            padding: '0 24px',
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <a
            href="/"
            style={{ fontSize: 16, fontWeight: 800, color: '#fff', textDecoration: 'none' }}
          >
            SkipTheDealerships.ai
          </a>
          <a
            href="/dealers"
            style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', textDecoration: 'none' }}
          >
            Dealer Program Info →
          </a>
        </div>

        <div style={{ maxWidth: 620, margin: '0 auto', padding: '40px 24px 80px' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: c.teal,
                marginBottom: 8,
              }}
            >
              Dealer Partner Program
            </div>
            <h1
              style={{
                margin: '0 0 10px',
                fontSize: 'clamp(24px,4vw,34px)',
                fontWeight: 800,
                color: c.navy,
                letterSpacing: '-0.5px',
              }}
            >
              Register your dealership
            </h1>
            <p style={{ margin: 0, fontSize: 15, color: c.muted, lineHeight: 1.6 }}>
              Get your storefront live and start receiving qualified leads.
            </p>
          </div>

          {/* Card */}
          <div
            style={{
              background: c.white,
              border: `1px solid ${c.border}`,
              borderRadius: 20,
              padding: 'clamp(24px,5vw,40px)',
            }}
          >
            <ProgressBar step={step} />

            {stepContent}

            {apiError && (
              <div
                style={{
                  marginTop: 16,
                  padding: '12px 16px',
                  background: '#FEF2F2',
                  border: '1px solid #FECACA',
                  borderRadius: 10,
                  fontSize: 13,
                  color: c.red,
                }}
              >
                {apiError}
              </div>
            )}

            {/* Navigation */}
            <div
              style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28, gap: 12 }}
            >
              {step > 0 ? (
                <button
                  onClick={() => setStep(s => s - 1)}
                  style={{
                    padding: '12px 24px',
                    borderRadius: 10,
                    border: `1px solid ${c.border}`,
                    background: 'none',
                    fontSize: 14,
                    fontWeight: 600,
                    color: c.muted,
                    cursor: 'pointer',
                  }}
                >
                  ← Back
                </button>
              ) : (
                <div />
              )}

              {step < STEPS.length - 1 ? (
                <button
                  onClick={next}
                  style={{
                    padding: '12px 32px',
                    borderRadius: 10,
                    border: 'none',
                    background: c.teal,
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: 'pointer',
                    flex: step === 0 ? 1 : 'none',
                  }}
                >
                  Continue →
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={submitting}
                  style={{
                    padding: '12px 32px',
                    borderRadius: 10,
                    border: 'none',
                    background: submitting ? c.muted : c.teal,
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                  }}
                >
                  {submitting ? 'Submitting…' : 'Submit Application →'}
                </button>
              )}
            </div>
          </div>

          <p style={{ textAlign: 'center', fontSize: 12, color: c.muted, marginTop: 20 }}>
            No credit card required · Canada & US dealers and wholesalers · Response within 1
            business day
          </p>
        </div>
      </div>
    </>
  );
}
